/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { Formik, FormikConfig, FormikProps } from 'formik';
import _ from 'lodash';
import {
  DEFAULT_TEMPLATE_RESOLUTION,
  TEMPLATE_RESOLUTION_LIST,
  TemplateResolutionKey,
} from 'modules/manager/domain/template-resolutions';
import {
  DEFAULT_TEMPLATE_TYPE,
  TEMPLATE_TYPE_REGISTRY,
  TemplateType,
  availableTemplateTypes,
  getTemplateTypeEntry,
} from 'modules/manager/domain/template-type';
import { normalizeTemplateName } from 'modules/manager/domain/template-name-conflict';
import { modalButton, modalError, modalInputStyle } from 'modules/template-editor/ui/components/elements/modals/styles';
import React from 'react';
import UDFormInput from 'ud-form/components/input';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import { Button } from 'ud-ui/components/button/styles';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';

export type ResolutionOption = SelectOption<TemplateResolutionKey>;
export type TemplateTypeOption = SelectOption<TemplateType>;
export type SubtypeOption = SelectOption<string>;

export type MediaManagerTemplateFormValues = {
  name: string;
  resolution: ResolutionOption;
  templateType: TemplateTypeOption;
  /** Single subtype option, or `null` when the type has no subtype dimension or none picked yet. */
  subtype: SubtypeOption | null;
};

type Props = FormikConfig<MediaManagerTemplateFormValues> & {
  existingNames: string[];
  isLoading: boolean;
  submitLabel?: string;
  /** Error from the last failed submit (e.g. the DB rejected the name). */
  errorMessage?: string | null;
};

export const RESOLUTION_OPTIONS: ResolutionOption[] = TEMPLATE_RESOLUTION_LIST.map((r) => ({
  value: r.key,
  label: r.label,
}));

export const DEFAULT_RESOLUTION_OPTION: ResolutionOption = RESOLUTION_OPTIONS.find(
  (o) => o.value === DEFAULT_TEMPLATE_RESOLUTION.key,
)!;

export const getResolutionOption = (key: TemplateResolutionKey): ResolutionOption =>
  RESOLUTION_OPTIONS.find((o) => o.value === key) ?? DEFAULT_RESOLUTION_OPTION;

export const TEMPLATE_TYPE_OPTIONS: TemplateTypeOption[] = availableTemplateTypes().map((t) => ({
  value: t.value,
  label: t.label,
}));

export const DEFAULT_TEMPLATE_TYPE_OPTION: TemplateTypeOption =
  TEMPLATE_TYPE_OPTIONS.find((o) => o.value === DEFAULT_TEMPLATE_TYPE) ?? TEMPLATE_TYPE_OPTIONS[0];

export const getTemplateTypeOption = (type: TemplateType): TemplateTypeOption =>
  TEMPLATE_TYPE_OPTIONS.find((o) => o.value === type) ?? DEFAULT_TEMPLATE_TYPE_OPTION;

export function getSubtypeOptions(type: TemplateType): readonly SubtypeOption[] {
  return getTemplateTypeEntry(type).subtypes?.options.map((o) => ({ value: o.value, label: o.label })) ?? [];
}

export function getSubtypeOptionForValue(type: TemplateType, value: string | undefined): SubtypeOption | null {
  if (!value) return null;
  const opt = getSubtypeOptions(type).find((o) => o.value === value);
  return opt ?? null;
}

export function buildInitialSubtype(type: TemplateType, current: readonly string[] | undefined): SubtypeOption | null {
  const cfg = getTemplateTypeEntry(type).subtypes;
  if (!cfg) return null;
  return getSubtypeOptionForValue(type, current?.[0]);
}

const validator = (existingNames: string[]) =>
  yup.object({
    name: yup
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .test('media-manager-template-name', 'Invalid name', function (value) {
        if (!value || value.length <= 0) {
          return this.createError({ message: 'Название шаблона не может быть пустым' });
        }
        // Case-insensitive, like the DB's alias_tournament_name unique rule — a
        // stricter DB would otherwise reject a name this check just approved.
        if (existingNames.map(normalizeTemplateName).includes(normalizeTemplateName(value))) {
          return this.createError({ message: 'Такое название уже существует, придумайте новое' });
        }
        return true;
      }),
    resolution: yup.object({ value: yup.string().required(), label: yup.string().required() }).required('Выберите разрешение'),
    templateType: yup.object({ value: yup.string().required(), label: yup.string().required() }).required('Выберите тип шаблона'),
    subtype: yup
      .object({ value: yup.string().required(), label: yup.string().required() })
      .nullable()
      // No template type declares a subtype dimension today, so this never
      // fires — the machinery is kept because the shape is generic and a future
      // type may want it. The message is derived from the config rather than
      // hardcoded («Выберите показатель») so it cannot outlive the one type that
      // used to have one.
      .test('subtype-required', function (value) {
        const type = (this.parent as MediaManagerTemplateFormValues).templateType?.value as TemplateType | undefined;
        if (!type) return true;
        const cfg = TEMPLATE_TYPE_REGISTRY[type].subtypes;
        if (!cfg || !cfg.required) return true;
        if (value) return true;
        return this.createError({ message: `Выберите: ${cfg.label.toLowerCase()}` });
      }),
  });

export const DEFAULT_TEMPLATE_FORM_VALUES: MediaManagerTemplateFormValues = {
  name: '',
  resolution: DEFAULT_RESOLUTION_OPTION,
  templateType: DEFAULT_TEMPLATE_TYPE_OPTION,
  subtype: null,
};

function MediaManagerTemplateForm(props: Props) {
  const { existingNames, isLoading, submitLabel = 'Создать', errorMessage, ...rest } = props;

  const getErrors = (formProps: FormikProps<MediaManagerTemplateFormValues>): string | undefined => {
    const error = _.find(formProps.errors, (err, key) => !!err && !!formProps.touched[key as keyof MediaManagerTemplateFormValues]);
    if (!error) return errorMessage ?? undefined;
    return typeof error === 'string' ? error : Object.values(error).find((v): v is string => typeof v === 'string');
  };

  return (
    <Formik validateOnChange validationSchema={validator(existingNames)} {...rest}>
      {(formProps) => {
        const activeType = formProps.values.templateType.value as TemplateType;
        const subtypeCfg = TEMPLATE_TYPE_REGISTRY[activeType].subtypes;
        const subtypeOptions = subtypeCfg ? getSubtypeOptions(activeType) : [];

        return (
          <form onSubmit={formProps.handleSubmit} className="w-[450px]">
            <Modal.Body className="flex flex-col gap-5">
              <UDFormInput
                readOnly={isLoading}
                onChangeCapture={() => {
                  formProps.values.name && !formProps.touched.name && formProps.setFieldTouched('name');
                }}
                name="name"
                autoFocus
                css={modalInputStyle}
                placeholder="Название шаблона"
              />
              <UDFormSelect
                name="resolution"
                label="Разрешение"
                placeholder="Выберите разрешение"
                options={RESOLUTION_OPTIONS}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <UDFormSelect
                name="templateType"
                label="Тип шаблона"
                placeholder="Выберите тип"
                options={TEMPLATE_TYPE_OPTIONS}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onChange={() => {
                  // Reset the subtype whenever the type changes — the previously
                  // chosen subtype is unlikely to be valid for the new type's option set.
                  formProps.setFieldValue('subtype', null);
                  formProps.setFieldTouched('subtype', false, false);
                }}
              />
              {subtypeCfg && (
                <UDFormSelect
                  name="subtype"
                  label={subtypeCfg.label}
                  placeholder={`Выберите: ${subtypeCfg.label.toLowerCase()}`}
                  options={subtypeOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            </Modal.Body>
            <Modal.Footer justify="start">
              <Button
                type="submit"
                className={classNames('flex-center', isLoading && 'loading')}
                disabled={!formProps.isValid || formProps.values.name.trim() === '' || isLoading}
                css={modalButton}
              >
                {submitLabel}
              </Button>
              <span css={modalError}>{getErrors(formProps)}</span>
            </Modal.Footer>
          </form>
        );
      }}
    </Formik>
  );
}

export default MediaManagerTemplateForm;
